/**
 * Project display-unit contract, independent of source data and engine units.
 * Mirror of backend config/MeasurementSystem.js; guarded by a parity test.
 * ASCII unit codes are data identifiers; UI formatting will render m³, m², etc.
 */
export const MEASUREMENT_SYSTEMS = Object.freeze(['metric', 'imperial'] as const)
export type MeasurementSystem = typeof MEASUREMENT_SYSTEMS[number]

export const DEFAULT_MEASUREMENT_SYSTEM: MeasurementSystem = 'metric'

export interface MeasurementProfile {
  readonly cargoLength: 'cm' | 'in'
  readonly vehicleLength: 'm' | 'ft'
  readonly mass: 'kg' | 'lb'
  readonly volume: 'm3' | 'ft3'
  readonly area: 'm2' | 'ft2'
  readonly surfaceLoad: 'kg/m2' | 'lb/ft2'
}

export const MEASUREMENT_PROFILES = Object.freeze({
  metric: Object.freeze({
    cargoLength: 'cm',
    vehicleLength: 'm',
    mass: 'kg',
    volume: 'm3',
    area: 'm2',
    surfaceLoad: 'kg/m2',
  } as const),
  imperial: Object.freeze({
    cargoLength: 'in',
    vehicleLength: 'ft',
    mass: 'lb',
    volume: 'ft3',
    area: 'ft2',
    surfaceLoad: 'lb/ft2',
  } as const),
} satisfies Record<MeasurementSystem, MeasurementProfile>)

/** Does not silently turn an unknown system into the default system. */
export function isMeasurementSystem(value: unknown): value is MeasurementSystem {
  return typeof value === 'string' && MEASUREMENT_SYSTEMS.some(system => system === value)
}
